import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  landedItemKeys,
  splitImportRows,
} from "../lib/optroImportPlaceholders";
import { OptroImportJob } from "../models/audit/optroSync";

interface UseImportPlaceholdersArgs<TItem, TRow> {
  job: OptroImportJob | null;
  startItems: TItem[];
  rows: TRow[];
  // Every list `onItemLanded` reloads, as the arrays currently held — the
  // rows first, plus any list the rows are grouped by. A fetch hook replaces
  // its array on each successful response, so once every entry differs from
  // the one held when an item landed (and nothing is loading), the reload
  // has come back with that item's final row. A failed reload keeps its
  // array, so the placeholder stays until a later fetch succeeds.
  reloadedLists: readonly unknown[];
  // Whether any of `reloadedLists` is still being fetched.
  loading: boolean;
  // Off while filters are applied — a placeholder can't be matched against
  // them, so the list shows only what the server returned.
  enabled: boolean;
  onItemLanded: () => void;
  // Module-level functions, so the memo below isn't redone every render.
  statusRefOf: (item: TItem) => string;
  itemKeyOf: (item: TItem) => string;
  rowKeyOf: (row: TRow) => string | null;
}

const NOTHING_AWAITED: ReadonlyMap<string, readonly unknown[]> = new Map();

// Placeholder rows for an Optro import's unfinished items, and the loaded
// rows with those items' half-made rows held back. Each time an item lands,
// `onItemLanded` reloads the list, so it fills in one row at a time rather
// than when the whole job ends; the item keeps its placeholder until that
// reload has come back.
export const useImportPlaceholders = <TItem, TRow>({
  job,
  startItems,
  rows,
  reloadedLists,
  loading,
  enabled,
  onItemLanded,
  statusRefOf,
  itemKeyOf,
  rowKeyOf,
}: UseImportPlaceholdersArgs<TItem, TRow>) => {
  const landed = useMemo(
    () => landedItemKeys(job, startItems, { statusRefOf, itemKeyOf }),
    [job, startItems, statusRefOf, itemKeyOf],
  );
  // Landed item key -> the lists held when it landed.
  const [awaitingReload, setAwaitingReload] =
    useState<ReadonlyMap<string, readonly unknown[]>>(NOTHING_AWAITED);
  const reloadedListsRef = useRef(reloadedLists);
  reloadedListsRef.current = reloadedLists;

  // Seeded with the current job so remounting the page mid- or post-import
  // doesn't reload for items the initial fetch already covers.
  const hasJob = job !== null;
  const seenLandedRef = useRef({ hadJob: hasJob, landed });
  const baselineReloadedRef = useRef(false);
  // Layout effects throughout: the reload flips `loading` on, and the
  // awaiting set updates, before paint — so a placeholder never blinks out
  // ahead of its real row, and a cached half-made row never shows.
  useLayoutEffect(() => {
    const seen = seenLandedRef.current;
    // The first job to appear after mount (e.g. the import listing arriving
    // on page load) reports items the page's own first fetch already covers,
    // so its landed set is the baseline: nothing is marked awaiting and no
    // reload fires.
    const before = seen.hadJob ? seen.landed : landed;
    seenLandedRef.current = { hadJob: hasJob, landed };
    // The baseline assumes the page's own fetch already covers those items,
    // but an item can finish between that fetch and this first registry
    // result, and would then never count as newly landed — its row would be
    // missing until the next manual reload. So when the first result already
    // has landed items, reload once. Once per mount, so a later job appearing
    // doesn't repeat it.
    if (!seen.hadJob && hasJob && landed.size > 0) {
      if (!baselineReloadedRef.current) {
        baselineReloadedRef.current = true;
        onItemLanded();
      }
      return;
    }
    const newlyLanded = [...landed].filter((key) => !before.has(key));
    if (newlyLanded.length === 0) return;
    const heldLists = reloadedListsRef.current;
    setAwaitingReload((current) => {
      const next = new Map(current);
      for (const key of newlyLanded) next.set(key, heldLists);
      return next;
    });
    onItemLanded();
  }, [hasJob, landed, onItemLanded]);

  useLayoutEffect(() => {
    if (loading || awaitingReload.size === 0) return;
    const reloaded = [...awaitingReload]
      .filter(([, heldLists]) =>
        heldLists.every((list, index) => reloadedLists[index] !== list),
      )
      .map(([key]) => key);
    if (reloaded.length === 0) return;
    setAwaitingReload((current) => {
      const next = new Map(current);
      for (const key of reloaded) next.delete(key);
      return next;
    });
  }, [loading, awaitingReload, reloadedLists]);

  return useMemo(() => {
    if (!enabled || !job) {
      const none: TItem[] = [];
      return { visibleRows: rows, importing: none };
    }
    return splitImportRows({
      job,
      startItems,
      rows,
      awaitingReload: new Set(awaitingReload.keys()),
      statusRefOf,
      itemKeyOf,
      rowKeyOf,
    });
  }, [
    enabled,
    job,
    startItems,
    rows,
    awaitingReload,
    statusRefOf,
    itemKeyOf,
    rowKeyOf,
  ]);
};
